// Slack-style dark theme — the only theme.
//
// Pass 2.10 collapsed the previous two-theme system (slack +
// records) into a single dark Slack palette. The records theme,
// the toggle UI, the persisted theme key, and every parameterized
// applyTheme/getStoredTheme/setStoredTheme function are gone. The
// CSS-custom-property delivery model is unchanged: applyTheme()
// (now nullary) writes a fixed token set onto <html> at boot.
//
// The token names below were extracted from app.slack.com via
// getComputedStyle on a live page (Pass 1.3a) and are
// authoritative; do not "round" them. The transparent-sidebar-
// over-app idiom means bg-app is darker than bg-message-pane so
// the sidebar's transparency reveals a deliberate dark surface.
//
// avatarPaletteIndex is preserved unchanged — it's the
// theme-agnostic deterministic-color helper consumed by the
// avatar component.

const LEGACY_THEME_STORAGE_KEY = "slack-archive-viewer:theme";

interface ThemeTokens {
  // Phase 7.0 Stage 1 — visual identity foundation. Deep neutral
  // canvas (with a faint violet undertone) replaces the old purple
  // base. canvas → surface → elevated is the three-step depth ramp;
  // surface/elevated are foundation tokens for Stage 2's frosted
  // card migration (not yet wired to product surfaces).
  "bg-canvas": string;
  "bg-canvas-surface": string;
  "bg-canvas-elevated": string;

  // Surfaces
  "bg-app": string;
  "bg-sidebar": string;
  "bg-sidebar-icon": string;
  "bg-sidebar-icon-active": string;
  "bg-sidebar-icon-hover": string;
  "bg-sidebar-icon-active-radius": string;
  "bg-conv-list": string;
  "bg-message-pane": string;
  "bg-message-hover": string;
  "bg-conv-active": string;
  "bg-conv-hover": string;
  "bg-thread-highlight": string;
  "bg-info-panel": string;
  "bg-card": string;
  "bg-code": string;
  "bg-chip-subtle": string;
  "bg-drop-zone": string;
  "bg-drop-zone-hover": string;
  "bg-drop-zone-active": string;

  // Text
  "text-primary": string;
  "text-body": string;
  "text-secondary": string;
  "text-tertiary": string;
  "text-muted": string;
  "text-timestamp": string;
  "text-sidebar-primary": string;
  "text-sidebar-secondary": string;
  "text-sidebar-icon": string;
  "text-sidebar-icon-active": string;

  // Borders / dividers
  "border-default": string;
  "border-emphasis": string;
  "border-active": string;
  "border-divider": string;
  "border-drop-zone": string;
  "border-drop-zone-active": string;

  // Accent
  "accent-bg": string;
  "accent-text": string;
  "accent-link": string;

  // Conversation list active row
  "conv-active-text": string;

  // Chips
  "chip-mention-bg": string;
  "chip-mention-text": string;
  "chip-mention-padding": string;
  "chip-mention-radius": string;
  "chip-channel-bg": string;
  "chip-channel-text": string;
  "chip-broadcast-bg": string;
  "chip-broadcast-text": string;

  // Date pill (separator)
  "date-pill-bg": string;
  "date-pill-text": string;
  "date-pill-font-size": string;
  "date-pill-font-weight": string;
  "date-pill-padding": string;
  "date-pill-radius": string;
  "date-pill-border": string;

  // Error
  "error-bg": string;
  "error-border": string;
  "error-text": string;

  // Status (Pass 2.17) — Drive backup state icons. A success green
  // + an amber for the in-progress spinner; queued/not_attempted
  // reuse the text-tertiary/-muted greys, failed reuses error-text.
  "success-text": string;
  "warning-text": string;

  // Mrkdwn (Pass 1.4) — code spans, code blocks, blockquotes
  "mrkdwn-code-bg": string;
  "mrkdwn-code-text": string;
  "mrkdwn-code-border": string;
  "mrkdwn-blockquote-border": string;
  "mrkdwn-blockquote-text": string;

  // Avatar
  "avatar-bg": string;
  "avatar-text": string;
  "avatar-0-bg": string;
  "avatar-1-bg": string;
  "avatar-2-bg": string;
  "avatar-3-bg": string;
  "avatar-4-bg": string;
  "avatar-5-bg": string;
  "avatar-fg": string;
  "avatar-radius": string;

  // Typography
  "font-family-sans": string;
  "font-size-message": string;
  "font-size-user-name": string;
  "font-size-timestamp": string;
  "font-size-conv-title": string;
  "font-size-section-heading": string;
  "font-weight-user-name": string;
  "font-weight-conv-title": string;
  "font-weight-section-heading": string;
  "line-height-message": string;
}

const SLACK_THEME: ThemeTokens = {
  // Surfaces — extracted from app.slack.com (dark Aubergine).
  // bg-app gets a perceptible purple cast (Pass 1.3a-fix); the
  // sidebar continues to use transparent over this surface, so a
  // single token change here tints the entire workspace + conv list
  // surround. The message pane stays neutral so the cross-fade
  // between purple sidebar and neutral content is the layout's
  // primary tonal cue.
  // Phase 7.0 S1.1 — deep neutral foundation. Faint violet
  // undertone (B slightly > R/G) keeps a hint of brand without the
  // old plum cast. canvas is the page base; surface/elevated are the
  // depth ramp for Stage 2.
  "bg-canvas": "rgb(10, 10, 15)",
  "bg-canvas-surface": "rgb(18, 18, 26)",
  "bg-canvas-elevated": "rgb(26, 26, 36)",
  // Phase 7.0 S1.1 — the page base shifts from plum to the deep
  // neutral canvas. Inputs/chips/nav bars that reference bg-app pick
  // this up automatically; surface/card tokens stay (Stage 2).
  "bg-app": "rgb(10, 10, 15)",
  "bg-sidebar": "transparent",
  "bg-sidebar-icon": "transparent",
  "bg-sidebar-icon-active": "rgba(239, 225, 245, 0.25)",
  "bg-sidebar-icon-hover": "rgba(239, 225, 245, 0.08)",
  "bg-sidebar-icon-active-radius": "8px",
  "bg-conv-list": "transparent",
  "bg-message-pane": "rgba(15, 15, 23, 0.72)",
  "bg-message-hover": "rgba(255, 255, 255, 0.03)",
  "bg-conv-active": "rgba(124, 92, 255, 0.24)",
  "bg-conv-hover": "rgba(239, 225, 245, 0.08)",
  "bg-thread-highlight": "rgba(248, 248, 248, 0.04)",
  "bg-info-panel": "rgba(15, 15, 23, 0.72)",
  "bg-card": "rgb(23, 23, 34)",
  "bg-code": "rgba(232, 232, 232, 0.05)",
  "bg-chip-subtle": "rgba(248, 248, 248, 0.04)",
  "bg-drop-zone": "rgb(23, 23, 34)",
  "bg-drop-zone-hover": "rgba(248, 248, 248, 0.04)",
  "bg-drop-zone-active": "rgba(43, 165, 206, 0.12)",

  // Text
  "text-primary": "rgb(248, 248, 248)",
  "text-body": "rgb(209, 210, 211)",
  "text-secondary": "rgba(227, 206, 235, 0.8)",
  "text-tertiary": "rgba(227, 206, 235, 0.6)",
  "text-muted": "rgba(227, 206, 235, 0.7)",
  "text-timestamp": "rgb(171, 171, 173)",
  "text-sidebar-primary": "rgb(248, 248, 248)",
  "text-sidebar-secondary": "rgba(227, 206, 235, 0.8)",
  "text-sidebar-icon": "rgb(248, 248, 248)",
  "text-sidebar-icon-active": "rgb(248, 248, 248)",

  // Borders / dividers
  "border-default": "rgba(121, 124, 129, 0.3)",
  "border-emphasis": "rgba(121, 124, 129, 0.6)",
  "border-active": "rgb(124, 92, 255)",
  "border-divider": "rgb(53, 55, 59)",
  "border-drop-zone": "rgba(121, 124, 129, 0.5)",
  "border-drop-zone-active": "rgb(124, 92, 255)",

  // Accent — Aurora shifts the brand from Slack aubergine to a
  // violet→indigo family. accent-bg drives buttons/active fills;
  // accent-link is the lighter on-dark link/active violet.
  "accent-bg": "rgb(124, 92, 255)",
  "accent-text": "rgb(245, 243, 255)",
  "accent-link": "rgb(167, 139, 250)",

  "conv-active-text": "rgb(237, 233, 254)",

  // Chips
  "chip-mention-bg": "rgba(3, 102, 150, 0.25)",
  "chip-mention-text": "rgb(43, 165, 206)",
  "chip-mention-padding": "1px 2px",
  "chip-mention-radius": "3px",
  "chip-channel-bg": "rgba(3, 102, 150, 0.25)",
  "chip-channel-text": "rgb(43, 165, 206)",
  "chip-broadcast-bg": "rgba(232, 145, 45, 0.25)",
  "chip-broadcast-text": "rgb(238, 178, 87)",

  // Date pill
  "date-pill-bg": "rgba(20, 20, 30, 0.85)",
  "date-pill-text": "rgb(209, 210, 211)",
  "date-pill-font-size": "13px",
  "date-pill-font-weight": "700",
  "date-pill-padding": "0 8px",
  "date-pill-radius": "24px",
  "date-pill-border": "1px solid rgba(121, 124, 129, 0.3)",

  // Error
  "error-bg": "rgba(224, 30, 90, 0.12)",
  "error-border": "rgb(224, 30, 90)",
  "error-text": "rgb(244, 164, 188)",

  // Status (Pass 2.17)
  "success-text": "rgb(72, 196, 144)",
  "warning-text": "rgb(222, 168, 56)",

  // Mrkdwn — Slack-faithful code styling on the dark surface.
  // Code text uses the link blue so inline code reads as "structured
  // identifier", visually distinct from plain body text. The block-
  // quote uses a soft white border consistent with Slack's actual.
  "mrkdwn-code-bg": "rgba(248, 248, 248, 0.06)",
  "mrkdwn-code-text": "rgb(43, 165, 206)",
  "mrkdwn-code-border": "rgba(248, 248, 248, 0.1)",
  "mrkdwn-blockquote-border": "rgba(248, 248, 248, 0.2)",
  "mrkdwn-blockquote-text": "rgb(209, 210, 211)",

  // Avatar — Pass 1.3a-fix: bumped saturation to read closer to real
  // Slack avatars. All 6 still pass WCAG AA against white text
  // (min 5.11:1 amber, max 14.00:1 deep aubergine).
  "avatar-bg": "rgb(74, 21, 75)",
  "avatar-text": "rgb(255, 255, 255)",
  "avatar-0-bg": "rgb(74, 21, 75)",
  "avatar-1-bg": "rgb(28, 100, 163)",
  "avatar-2-bg": "rgb(20, 121, 73)",
  "avatar-3-bg": "rgb(170, 88, 5)",
  "avatar-4-bg": "rgb(165, 50, 50)",
  "avatar-5-bg": "rgb(90, 70, 145)",
  "avatar-fg": "rgb(255, 255, 255)",
  "avatar-radius": "8px",

  // Typography — Slack-faithful weights & sizes
  "font-family-sans":
    '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif',
  "font-size-message": "15px",
  "font-size-user-name": "15px",
  "font-size-timestamp": "12px",
  "font-size-conv-title": "18px",
  "font-size-section-heading": "15px",
  "font-weight-user-name": "900",
  "font-weight-conv-title": "900",
  "font-weight-section-heading": "500",
  "line-height-message": "22px",
};

const PALETTE_SIZE = 6;

// Stable hash → palette index. Determines which avatar background a
// user_id maps to. Same input always produces the same color across
// sessions, regardless of insertion order or workspace.
export function avatarPaletteIndex(seed: string): number {
  let h = 0;
  for (let i = 0; i < seed.length; i++) {
    h = ((h << 5) - h + seed.charCodeAt(i)) | 0;
  }
  return Math.abs(h) % PALETTE_SIZE;
}

/**
 * Write the dark Slack theme tokens onto <html> as CSS custom
 * properties. Idempotent. Called once at app boot before the first
 * render; nothing else needs to call it.
 */
export function applyTheme(): void {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  for (const key of Object.keys(SLACK_THEME) as Array<keyof ThemeTokens>) {
    root.style.setProperty(`--theme-${key}`, SLACK_THEME[key]);
  }
  root.setAttribute("data-theme", "slack");
}

/**
 * Pass 2.10 one-time cleanup: remove the legacy
 * `slack-archive-viewer:theme` localStorage key written by pre-2.10
 * builds. The key has no consumer in the current code; leaving it
 * in storage is harmless but clutters the dev-tools view of the
 * application's storage.
 */
export function clearLegacyThemeStorage(): void {
  if (typeof localStorage === "undefined") return;
  try {
    if (localStorage.getItem(LEGACY_THEME_STORAGE_KEY) !== null) {
      localStorage.removeItem(LEGACY_THEME_STORAGE_KEY);
    }
  } catch {
    // Private mode / disabled storage — silently skip.
  }
}
